/**
 * Window polyfill for React Native environments
 * 
 * This polyfill ensures that window.addEventListener and window.removeEventListener
 * are available in React Native environments, which is needed for React Query's
 * focusManager to work properly during hot reload.
 */

import { Platform } from 'react-native';

// Only apply polyfills in React Native environments
if (Platform.OS !== 'web' && typeof window !== 'undefined') {
  // Storage for event listeners to enable proper cleanup
  const eventListeners = new Map<string, Set<EventListener>>();

  // Polyfill addEventListener if not present
  if (!window.addEventListener) {
    // @ts-expect-error: relax overly specific DOM lib typings in RN
    window.addEventListener = function (type: string, listener: EventListener, options?: boolean | AddEventListenerOptions) {
      // Store the listener for potential cleanup
      if (!eventListeners.has(type)) {
        eventListeners.set(type, new Set());
      }
      eventListeners.get(type)!.add(listener);
      
      // In React Native, we can't actually listen to window events,
      // but we store them to prevent errors
      console.debug(`[Window Polyfill] addEventListener called for: ${type}`);
    };
  }

  // Polyfill removeEventListener if not present
  if (!window.removeEventListener) {
    // @ts-expect-error: relax overly specific DOM lib typings in RN
    window.removeEventListener = function (type: string, listener: EventListener, options?: boolean | EventListenerOptions) {
      // Remove the listener from our storage
      const listeners = eventListeners.get(type);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          eventListeners.delete(type);
        }
      }
      
      console.debug(`[Window Polyfill] removeEventListener called for: ${type}`);
    };
  }

  // Polyfill other common window methods that might be missing
  if (!window.dispatchEvent) {
    // @ts-ignore: relax overly specific DOM lib typings in RN
    window.dispatchEvent = function (event: Event) {
      console.debug(`[Window Polyfill] dispatchEvent called for: ${event.type}`);
      return true;
    };
  }

  // Clean up a previously-polyfilled fake `document` (e.g. left over from an older
  // version of this file after Fast Refresh). Leaving a body-less document around
  // tricks `@react-aria/utils/runAfterTransition` into calling
  // `document.body.addEventListener(...)` and crashing.
  if (window.document && !window.document.body) {
    try {
      // @ts-expect-error: deleting an injected polyfill on RN
      delete window.document;
    } catch {
      // @ts-expect-error: fall back to nulling if delete fails on the global
      window.document = undefined;
    }
  }

  // Intentionally do NOT polyfill `document`. On React Native `global.window === global`
  // (see react-native/Libraries/Core/setUpGlobals.js), so assigning `window.document = {...}`
  // creates a global `document`. Libraries like `@react-aria/utils/runAfterTransition` then
  // detect it at module load, call `setupGlobalEvents()`, and crash on
  // `document.body.addEventListener(...)` because `body` is undefined — surfacing as
  // "Cannot read property 'addEventListener' of undefined" during hot reload, which cascades
  // into every consumer (gluestack Button, AuthProvider, etc.) failing to evaluate.

  // Add focus/blur event handling that React Query's focusManager expects
  if (!window.focus) {
    window.focus = function () {
      console.debug('[Window Polyfill] focus called');
    };
  }

  if (!window.blur) {
    window.blur = function () {
      console.debug('[Window Polyfill] blur called');
    };
  }

  console.debug('[Window Polyfill] Window event listener polyfills applied for React Native');
}
